import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cogoToast from 'cogo-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faTimes } from '@fortawesome/pro-regular-svg-icons';

import Modal from '../General/Modal';
import { getCountryBlockBypassCodes, addCountryBlockBypassCode, removeCountryBlockBypassCode } from '../../api/registration_codes';

import './SignupCountryBlockBypassCodesModal.scss';

const SignupCountryBlockBypassCodesModal = ({ close }) => {
  const [codes, setCodes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [newCode, setNewCode] = useState('');

  useEffect(() => {
    getCountryBlockBypassCodes()
      .then(res => setCodes(res.codes || []))
      .catch(() => cogoToast.error('Error loading bypass codes'))
      .finally(() => setIsLoading(false));
  }, []);

  const addCode = () => {
    if (isSaving) return;
    const code = newCode.trim();
    if (!code) return cogoToast.error('Enter a registration code.');
    setIsSaving(true);
    addCountryBlockBypassCode(code)
      .then(res => {
        setCodes(prev => [res.code, ...prev.filter(c => c.id !== res.code.id)]);
        setNewCode('');
        cogoToast.success('Code flagged — creators from blocked countries can now sign up with it.');
      })
      .catch(err => cogoToast.error(err || 'Error flagging code.'))
      .finally(() => setIsSaving(false));
  };

  const removeCode = row => {
    if (isSaving) return;
    if (!window.confirm(`Stop letting ${row.code} bypass the country block?`)) return;
    const prevCodes = codes;
    setCodes(prev => prev.filter(c => c.id !== row.id));
    setIsSaving(true);
    removeCountryBlockBypassCode(row.id)
      .then(() => cogoToast.success(`${row.code} removed.`))
      .catch(() => {
        setCodes(prevCodes);
        cogoToast.error('Error removing code.');
      })
      .finally(() => setIsSaving(false));
  };

  return (
    <Modal visible={true} close={close}>
      <div className='signup-bypass-codes-modal-outer'>
        <div className='close' onClick={close}>
          <FontAwesomeIcon icon={faTimes} />
        </div>
        <div className='signup-bypass-codes-modal-title'>Country Block Bypass Codes</div>
        <div className='signup-bypass-codes-modal-subtitle'>
          <p>
            Signups from fraud-blocked countries (e.g. Morocco) are rejected at account creation. Flag an existing registration code here to let its
            signups through — useful for a vetted creator in a blocked country. The code must already exist.
          </p>
        </div>

        <div className='signup-bypass-codes-list'>
          {isLoading && <div className='signup-bypass-codes-empty'>Loading…</div>}
          {!isLoading && codes.length === 0 && <div className='signup-bypass-codes-empty'>No bypass codes.</div>}
          {!isLoading &&
            codes.map(row => (
              <div key={row.id} className='signup-bypass-codes-row'>
                <div className='signup-bypass-codes-row-code'>
                  <strong>{row.code}</strong>
                </div>
                <FontAwesomeIcon icon={faTrash} onClick={() => removeCode(row)} />
              </div>
            ))}
        </div>

        <div className='signup-bypass-codes-add'>
          <input
            type='text'
            placeholder='Registration code'
            value={newCode}
            onChange={e => setNewCode(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addCode()}
            disabled={isSaving}
          />
          <button onClick={addCode} disabled={isSaving || !newCode.trim()}>
            <FontAwesomeIcon icon={faPlus} /> Add
          </button>
        </div>
      </div>
    </Modal>
  );
};

SignupCountryBlockBypassCodesModal.propTypes = {
  close: PropTypes.func.isRequired
};

export default SignupCountryBlockBypassCodesModal;
